import { observer } from "mobx-react";
import { Checkbox, Alert, Card } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faRoute, faTruck, faMobileAlt, faMapMarkerAlt, faMap, faHeadset, faEdit, faMapPin, faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons";

interface ModuleSelectionStepProps {
  data: {
    tactical: boolean;
    routeView: boolean;
    dispatch: boolean;
    mobileApp: boolean;
    geocoding: boolean;
    mapEditor: boolean;
    serviceDesk: boolean;
    jobCreationEdit: boolean;
    trackingPage: boolean;
    blackboxRouting: boolean;
  };
  onChange: (data: any) => void;
}

const ModuleSelectionStep = observer(({ data, onChange }: ModuleSelectionStepProps) => {
  const handleModuleChange = (module: string, checked: boolean) => {
    const updates: any = { [module]: checked };

    // If enabling blackbox routing, disable all other modules except geocoding
    if (module === "blackboxRouting" && checked) {
      updates.tactical = false;
      updates.routeView = false;
      updates.dispatch = false;
      updates.mobileApp = false;
      updates.mapEditor = false;
      updates.serviceDesk = false;
      updates.jobCreationEdit = false;
      updates.trackingPage = false;
    }

    // If disabling blackbox routing, no special action needed
    if (module === "blackboxRouting" && !checked) {
      // Allow user to re-enable other modules
    }

    // If enabling any other module (except geocoding), disable blackbox routing
    if (module !== "blackboxRouting" && module !== "geocoding" && checked) {
      updates.blackboxRouting = false;
    }

    if (module === "dispatch" && !checked) {
      updates.mobileApp = false;
    }

    onChange({ ...data, ...updates });
  };

  const isBlackboxEnabled = data.blackboxRouting;

  const modules = [
    { key: "tactical", label: "Tactical Planning View", icon: faCalendarAlt, color: "#1890ff" },
    { key: "routeView", label: "Route View", icon: faRoute, color: "#52c41a" },
    { key: "dispatch", label: "Dispatch View", icon: faTruck, color: "#fa8c16" },
    { key: "mobileApp", label: "Mobile Application", icon: faMobileAlt, color: "#722ed1", indent: false, dependsOn: "dispatch" },
    { key: "serviceDesk", label: "Service Desk", icon: faHeadset, color: "#eb2f96" },
    { key: "jobCreationEdit", label: "Job Creation & Edit", icon: faEdit, color: "#13c2c2", indent: false, dependsOn: "serviceDesk" },
    { key: "trackingPage", label: "Tracking Page", icon: faMapPin, color: "#fa541c" },
    { key: "geocoding", label: "Geocoding", icon: faMapMarkerAlt, color: "#2f54eb", subtitle: "" },
    { key: "mapEditor", label: "Map Editor", icon: faMap, color: "#faad14" },
  ];

  return (
    <div className="space-y-6 p-3">
      <Alert message="Module Selection" description="Please select the modules that your client will use." type="info" showIcon className="mb-6" />

      {isBlackboxEnabled && (
        <Alert message="Blackbox Routing Enabled" description="When Blackbox Routing is enabled, all other modules (except Geocoding) are disabled." type="warning" showIcon className="mb-6" />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((module) => {
          const isDisabled = isBlackboxEnabled || (module.dependsOn && !data[module.dependsOn]);

          return (
            <Card key={module.key} size="small" className={`${module.indent ? "ml-8" : ""} transition-all hover:shadow-md ${isDisabled ? "opacity-50" : ""}`} bodyStyle={{ padding: "12px 16px" }}>
              <Checkbox checked={data[module.key]} onChange={(e) => handleModuleChange(module.key, e.target.checked)} disabled={isDisabled} className="w-full">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={module.icon} style={{ color: module.color, fontSize: "18px", marginRight: "12px", marginLeft: "10px" }} />
                  <div className="flex-1">
                    <span className="font-medium text-base">{module.label}</span>
                    {module.subtitle && <div className="text-xs text-gray-500 mt-1">{module.subtitle}</div>}
                  </div>
                </div>
              </Checkbox>
            </Card>
          );
        })}
      </div>

      <Card className="mt-6 border-orange-300 bg-orange-50" size="small">
        <Checkbox checked={data.blackboxRouting} onChange={(e) => handleModuleChange("blackboxRouting", e.target.checked)}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCloudUploadAlt} style={{ color: "#fa8c16", fontSize: "18px", marginRight: "12px" }} />
            <div>
              <span className="font-medium text-base text-orange-600">Blackbox Routing</span>
              <div className="text-xs text-gray-600 mt-1">(Enabling this disables all other modules except Geocoding)</div>
            </div>
          </div>
        </Checkbox>
      </Card>
    </div>
  );
});

export default ModuleSelectionStep;
